import { useEffect, useMemo, useState } from 'react';
import { UserCheck, Check, X } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import SearchInput from '../components/SearchInput';
import { profileCorrectionsAPI } from '../services/api';
import { filterBySearch } from '../utils/search';

const FIELD_LABELS = {
  mobile_number: 'Mobile',
  email: 'Email',
  pan: 'PAN',
  aadhaar: 'Aadhaar',
};

export default function ProfileCorrections() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [remarks, setRemarks] = useState({});
  const [acting, setActing] = useState(null);
  const [search, setSearch] = useState('');

  const filteredRequests = useMemo(
    () => filterBySearch(requests, search, [
      'request_number',
      'employee_name',
      'employee_code',
      'field_name',
      'status',
      'reason',
    ]),
    [requests, search]
  );

  const load = () => profileCorrectionsAPI.list({ status: filter || undefined })
    .then(({ data }) => setRequests(data))
    .finally(() => setLoading(false));

  useEffect(() => {
    setLoading(true);
    load();
  }, [filter]);

  const approve = async (id) => {
    setActing(id);
    try {
      await profileCorrectionsAPI.approve(id, { remarks: remarks[id] });
      await load();
    } finally {
      setActing(null);
    }
  };

  const reject = async (id) => {
    if (!remarks[id]?.trim()) {
      alert('Rejection remarks are required');
      return;
    }
    setActing(id);
    try {
      await profileCorrectionsAPI.reject(id, { remarks: remarks[id] });
      await load();
    } finally {
      setActing(null);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <UserCheck className="w-7 h-7 text-primary-600" />
          Profile Corrections
        </h1>
        <p className="text-slate-500 mt-1">Review employee KYC / contact correction requests</p>
      </div>

      <div className="flex gap-2">
        {['pending', 'approved', 'rejected', ''].map((s) => (
          <button
            key={s || 'all'}
            type="button"
            onClick={() => setFilter(s)}
            className={`px-3 py-1 rounded-full text-sm capitalize ${filter === s ? 'bg-primary-600 text-white' : 'bg-slate-100 text-slate-600'}`}
          >
            {s || 'all'}
          </button>
        ))}
      </div>

      <SearchInput
        className="max-w-md"
        placeholder="Search by employee, request number, field…"
        value={search}
        onChange={setSearch}
      />

      {requests.length === 0 ? (
        <p className="text-slate-500">No correction requests.</p>
      ) : filteredRequests.length === 0 ? (
        <p className="text-slate-500">No requests match your search.</p>
      ) : (
        <div className="space-y-4">
          {filteredRequests.map((r) => (
            <div key={r.id} className="card text-sm space-y-3">
              <div className="flex justify-between gap-2">
                <div>
                  <p className="font-semibold">{r.request_number}</p>
                  <p className="text-slate-500">{r.employee_name} ({r.employee_code})</p>
                </div>
                <span className="capitalize px-2 py-0.5 rounded-full text-xs bg-slate-100 h-fit">{r.status}</span>
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                <p><span className="text-slate-400">Field:</span> {FIELD_LABELS[r.field_name]}</p>
                <p><span className="text-slate-400">Submitted:</span> {new Date(r.submitted_at).toLocaleString('en-IN')}</p>
                <p><span className="text-slate-400">Current:</span> {r.current_value_masked || '—'}</p>
                <p><span className="text-slate-400">Proposed:</span> {r.proposed_value_masked}</p>
              </div>
              <p><span className="text-slate-400">Reason:</span> {r.reason}</p>
              {r.status === 'pending' && (
                <>
                  <textarea
                    className="input-field text-sm"
                    rows={2}
                    placeholder="Remarks (required for rejection)"
                    value={remarks[r.id] || ''}
                    onChange={(e) => setRemarks({ ...remarks, [r.id]: e.target.value })}
                  />
                  <div className="flex gap-2">
                    <button type="button" className="btn-primary text-sm flex items-center gap-1" disabled={acting === r.id} onClick={() => approve(r.id)}>
                      <Check className="w-4 h-4" /> Approve
                    </button>
                    <button type="button" className="btn-secondary text-sm text-red-700 flex items-center gap-1" disabled={acting === r.id} onClick={() => reject(r.id)}>
                      <X className="w-4 h-4" /> Reject
                    </button>
                  </div>
                </>
              )}
              {r.review_remarks && <p className="text-slate-500 italic">Review: {r.review_remarks}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
